import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HomeworkTracker, HomeworkLegend } from "@/components/HomeworkTracker";
import { GradeBadge } from "@/components/GradeBadge";
import { ArrowLeft, Table as TableIcon, LayoutGrid, CalendarX, MessageSquare } from "lucide-react";

const CycleBlock = ({ label, cycle, computedGrade, hwGrade }) => (
    <div className="border border-slate-200 rounded-xl p-4 bg-bone/40">
        <div className="flex items-center justify-between mb-3">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Cikli</div>
                <div className="font-heading font-bold text-slate-800">{label}</div>
            </div>
            <GradeBadge grade={computedGrade} size="sm" />
        </div>
        <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Detyrat (10)</div>
            <div className="text-[11px] font-bold text-oxford">{hwGrade.toFixed(2)} / 5</div>
        </div>
        <HomeworkTracker statuses={cycle.homework} />
        <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="flex items-center justify-between gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Testi</span>
                <span className="text-sm font-bold text-slate-800">{cycle.test_score == null ? "—" : `${cycle.test_score}/5`}</span>
            </div>
            <div className="flex items-center justify-between gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Projekti</span>
                <span className="text-sm font-bold text-slate-800">{cycle.project_score == null ? "—" : `${cycle.project_score}/5`}</span>
            </div>
            <div className="flex items-center justify-between gap-1.5" title="Opsionale">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Goje</span>
                <span className="text-sm font-bold text-slate-800">{cycle.oral_score == null ? "—" : `${cycle.oral_score}/5`}</span>
            </div>
        </div>
    </div>
);

const SubjectCard = ({ record, semester }) => {
    const sem = record.semesters[String(semester)];
    const computed = record.computed[String(semester)];
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6" data-testid={`student-subject-card-${record.subject_id}-sem${semester}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {record.subject_type === "professional" ? "Lëndë Profesionale" : "Lëndë e Përgjithshme"}
                    </div>
                    <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-900 mt-1 truncate">{record.subject_name}</h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        {record.absences_hours > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-terracotta/10 text-terracotta text-[11px] font-bold border border-terracotta/30">
                                <CalendarX className="w-3 h-3" /> {record.absences_hours} orë
                            </span>
                        )}
                        {record.comments_count > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-seablue/10 text-seablue text-[11px] font-bold border border-seablue/30">
                                <MessageSquare className="w-3 h-3" /> {record.comments_count}
                            </span>
                        )}
                    </div>
                </div>
                <div className="text-center shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Përfundimtare</div>
                    <GradeBadge grade={computed.final} size="md" />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CycleBlock label="Nota 1" cycle={sem["1"]} computedGrade={computed.grade1} hwGrade={computed.hw1} />
                <CycleBlock label="Nota 2" cycle={sem["2"]} computedGrade={computed.grade2} hwGrade={computed.hw2} />
            </div>
        </div>
    );
};

const TableRow = ({ record, semester }) => {
    const computed = record.computed[String(semester)];
    return (
        <tr className="border-b border-slate-200 hover:bg-bone/30">
            <td className="px-3 py-3"><div className="font-bold text-slate-900 text-sm truncate">{record.subject_name}</div></td>
            <td className="px-2 py-3 text-center"><GradeBadge grade={computed.grade1} size="sm" /></td>
            <td className="px-2 py-3 text-center"><GradeBadge grade={computed.grade2} size="sm" /></td>
            <td className="px-2 py-3 text-center"><GradeBadge grade={computed.final} size="sm" /></td>
        </tr>
    );
};

export default function StudentGrades() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("cards"); // cards | table

    useEffect(() => {
        (async () => {
            try {
                const m = await api.get("/student/me");
                if (m.data?.student?.id) {
                    const r = await api.get(`/students/${m.data.student.id}/records`);
                    setData(r.data);
                }
            } finally { setLoading(false); }
        })();
    }, []);

    const overallSem = useMemo(() => {
        if (!data) return { 1: null, 2: null };
        const calc = (sem) => {
            const finals = data.records.map((r) => r.computed[sem].final).filter((v) => v != null);
            return finals.length ? Math.round(finals.reduce((a, b) => a + b, 0) / finals.length * 10) / 10 : null;
        };
        return { 1: calc("1"), 2: calc("2") };
    }, [data]);

    return (
        <Layout>
            <Link to="/student" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="student-grades-back">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            {loading ? <div>Duke ngarkuar…</div> : !data ? <div>Pa të dhëna.</div> : (
                <>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Notat e mia</div>
                            <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{data.student.name}</h1>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 flex gap-4 sm:gap-6 self-start md:self-auto">
                            <div className="text-center"><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sem 1</div><GradeBadge grade={overallSem[1]} size="md" /></div>
                            <div className="text-center"><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sem 2</div><GradeBadge grade={overallSem[2]} size="md" /></div>
                        </div>
                    </div>

                    <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                        <HomeworkLegend />
                        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 shadow-sm" data-testid="student-grades-view-switch">
                            <button onClick={() => setView("cards")} data-testid="student-grades-view-cards"
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition ${view === "cards" ? "bg-oxford text-white" : "text-slate-600 hover:bg-bonehover"}`}>
                                <LayoutGrid className="w-3.5 h-3.5" /> Karta
                            </button>
                            <button onClick={() => setView("table")} data-testid="student-grades-view-table"
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition ${view === "table" ? "bg-oxford text-white" : "text-slate-600 hover:bg-bonehover"}`}>
                                <TableIcon className="w-3.5 h-3.5" /> Tabelë
                            </button>
                        </div>
                    </div>

                    <Tabs defaultValue="1" className="w-full">
                        <TabsList className="bg-white border border-slate-200 h-11 p-1 mb-4">
                            <TabsTrigger value="1" className="px-6 data-[state=active]:bg-oxford data-[state=active]:text-white">Semestri 1</TabsTrigger>
                            <TabsTrigger value="2" className="px-6 data-[state=active]:bg-oxford data-[state=active]:text-white">Semestri 2</TabsTrigger>
                        </TabsList>
                        {["1", "2"].map((s) => (
                            <TabsContent key={s} value={s}>
                                {view === "cards" ? (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={`student-grades-cards-sem${s}`}>
                                        {data.records.map((r) => <SubjectCard key={r.id} record={r} semester={s} />)}
                                    </div>
                                ) : (
                                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                                        <table className="w-full">
                                            <thead className="bg-bone/60 text-[10px] uppercase tracking-widest text-slate-600 font-bold">
                                                <tr><th className="text-left px-3 py-3">Lënda</th><th className="px-2 py-3">Nota 1</th><th className="px-2 py-3">Nota 2</th><th className="px-2 py-3">Përfund.</th></tr>
                                            </thead>
                                            <tbody>
                                                {data.records.map((r) => <TableRow key={r.id} record={r} semester={s} />)}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </TabsContent>
                        ))}
                    </Tabs>
                </>
            )}
        </Layout>
    );
}
